import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getSystemConfig } from 'src/backend/config';
import {
  getMaintenanceBypassToken,
  getMaintenanceModeSnapshot,
  setMaintenanceMode,
  subscribeToMaintenanceMode,
} from 'src/backend/maintenance';

const MAINTENANCE_POLL_INTERVAL_MS = 5000;

function MaintenanceScreen({ onRetry, retrying }) {
  const { t } = useTranslation('translation');

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'clip',
        bgcolor: 'common.white',
        px: 3,
        py: { xs: 4, sm: 6 },
      }}
    >
      <Stack
        role="status"
        aria-live="polite"
        alignItems="center"
        textAlign="center"
        sx={{ width: '100%', maxWidth: 440 }}
      >
        <Box
          component="img"
          src="/static/illustrations/under-maintenance.svg"
          alt=""
          width="295"
          height="250"
          fetchPriority="high"
          sx={{ width: 'min(100%, 295px)', height: 'auto' }}
        />

        <Typography
          component="h1"
          sx={{
            mt: { xs: 4, sm: 5 },
            minWidth: 0,
            color: 'grey.800',
            fontSize: { xs: '1.75rem', sm: '2rem' },
            fontWeight: 700,
            lineHeight: 1.2,
            overflowWrap: 'anywhere',
          }}
        >
          {t('maintenance.title')}
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            mt: 3.5,
            maxWidth: 390,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            lineHeight: 1.55,
          }}
        >
          {t('maintenance.description')}
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            mt: 3,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            lineHeight: 1.55,
          }}
        >
          {t('maintenance.prompt')}
        </Typography>

        <LoadingButton
          loading={retrying}
          onClick={onRetry}
          variant="contained"
          size="large"
          sx={{
            mt: 4,
            width: '100%',
            maxWidth: 404,
            minHeight: 56,
            borderRadius: 1,
            bgcolor: 'secondary.main',
            boxShadow: 'none',
            fontSize: '1.125rem',
            whiteSpace: 'nowrap',
            transition:
              'background-color 120ms cubic-bezier(0.16, 1, 0.3, 1), transform 120ms cubic-bezier(0.16, 1, 0.3, 1)',
            '&:hover': {
              bgcolor: 'secondary.500',
              boxShadow: 'none',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              bgcolor: 'secondary.600',
              transform: 'translateY(1px)',
            },
            '&.Mui-focusVisible': {
              outline: '3px solid',
              outlineColor: 'primary.main',
              outlineOffset: 3,
            },
          }}
        >
          {t('maintenance.retry')}
        </LoadingButton>
      </Stack>
    </Box>
  );
}

export default function MaintenanceGate({ children }) {
  const maintenanceActive = useSyncExternalStore(
    subscribeToMaintenanceMode,
    getMaintenanceModeSnapshot,
    getMaintenanceModeSnapshot
  );
  const mountedRef = useRef(true);
  const [initialCheckPending, setInitialCheckPending] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const checkMaintenanceMode = useCallback(
    async ({ initial = false, allowBypassRetry = false } = {}) => {
      if (initial && mountedRef.current) {
        setInitialCheckPending(true);
      }
      if (allowBypassRetry && mountedRef.current) {
        setRetrying(true);
      }

      try {
        const config = await getSystemConfig();
        const enabled = config?.maintenance_mode === true;
        const hasBypassToken = Boolean(getMaintenanceBypassToken());

        if (!enabled) {
          setMaintenanceMode(false);
        } else if (!hasBypassToken) {
          setMaintenanceMode(true);
        } else if (allowBypassRetry) {
          // Re-open the app so the corrected token can be validated by an
          // ordinary protected request. An invalid token will immediately
          // produce another maintenance 503 and close the gate again.
          setMaintenanceMode(false);
        }
      } catch {
        // Keep the current state. Ordinary request errors remain responsible
        // for their existing error UI, while an active maintenance screen
        // stays in place until /config confirms that maintenance has ended.
      } finally {
        if (mountedRef.current) {
          if (initial) {
            setInitialCheckPending(false);
          }
          if (allowBypassRetry) {
            setRetrying(false);
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    mountedRef.current = true;
    void checkMaintenanceMode({ initial: true });

    const handleFocus = () => void checkMaintenanceMode();
    window.addEventListener('focus', handleFocus);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkMaintenanceMode]);

  useEffect(() => {
    if (!maintenanceActive) {
      return undefined;
    }

    const interval = window.setInterval(
      () => void checkMaintenanceMode(),
      MAINTENANCE_POLL_INTERVAL_MS
    );
    return () => window.clearInterval(interval);
  }, [checkMaintenanceMode, maintenanceActive]);

  if (maintenanceActive) {
    return (
      <MaintenanceScreen
        retrying={retrying}
        onRetry={() => void checkMaintenanceMode({ allowBypassRetry: true })}
      />
    );
  }

  if (initialCheckPending) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return children;
}
